import type { SlugPageProps } from '@/cms/rendering-strategy/types'
import type { SlugMinimumStaticProps, SlugStaticProps } from '@/cms/types'
import type { PageProps as UniformClientProps } from '@/cms/uniform/uniformPageClient'
import type { Configuration } from '@/config/types'
import type { Translations } from '@/intl/types'
import type { Tokens } from '@/scopes/common/infrastructure/tokens/tokensRepository'
import type { StaticExportPath } from '@uniformdev/next-server'
import type { GetStaticProps, GetStaticPropsContext, GetStaticPropsResult } from 'next'
import { CMSNotFoundError } from '@/cms/errors/CMSNotFoundError'
import { getComponentIndex as defaultGetComponentIndex } from '@/cms/getComponentIndex'
import { getProductISRPaths, isAProductPage } from '@/cms/getProductISRPaths'
import { ISRPathHelper } from '@/cms/ISRPathHelper'
import { pickComponentsFromIndex as defaultPickComponentsFromIndex } from '@/cms/pickComponentsFromIndex'
import renderingStrategyAbstractFactory from '@/cms/rendering-strategy/renderingStrategyAbstractFactory'
import routeRepository from '@/cms/routes/routeRepository'
import { uniformPageClient } from '@/cms/uniform/uniformPageClient'
import { getBrandName, getSiteName, getWebsiteDomain, isExportProcess } from '@/helpers/environment'
import translationsRepository from '@/intl/translations'
import { reactPageRegistry as defaultReactPageRegistry } from '@/react-pages'
import configRepository from '@/scopes/common/infrastructure/config/configRepository'
import featureFlagClient from '@/scopes/common/infrastructure/feature-flags/featureFlagClient'
import tokensRepository from '@/scopes/common/infrastructure/tokens/tokensRepository'

// Because we defined this page as [...slug] the params.slug will be an array of strings
// https://nextjs.org/docs/app/building-your-application/routing/dynamic-routes#typescript
type StaticPropsContext = GetStaticPropsContext & {
  params: GetStaticPropsContext['params'] & { slug: string[] | undefined }
}

// Root page handling all pages coming from Uniform Page Service API
export function Page({
  reactComponentsList,
  reactComponentParameters,
  getComponentIndex = defaultGetComponentIndex,
  pickComponentsFromIndex = defaultPickComponentsFromIndex,
  reactPageRegistry = defaultReactPageRegistry,
  ...pageProps
}: SlugPageProps) {
  const renderingStrategy = renderingStrategyAbstractFactory.createRenderingStrategy(pageProps, reactPageRegistry)
  return renderingStrategy.renderPageComponent({
    reactComponentsList,
    reactComponentParameters,
    getComponentIndex,
    pickComponentsFromIndex,
    reactPageRegistry,
    ...pageProps,
  })
}

// Using Automatic Static Optimization
export async function getStaticPaths() {
  // await import is essential here because getStaticPaths only server-side and import must only happen server side (on client-side the code will blow up)
  const { getStaticPaths: getStaticPathsUniform } = await import('@uniformdev/next-server')

  if (isExportProcess()) {
    // If we are exporting the site directly or via a publish
    // specify all static paths and let nextjs handle 404
    const paths = await getStaticPathsUniform()

    const adjustedPaths = await getAdjustedStaticPaths(paths)
    return {
      paths: adjustedPaths,
      fallback: 'blocking',
    }
  }
  // If we are running the site in dynamic preview then
  // handle all paths and render them on the fly
  return {
    paths: [],
    fallback: 'blocking',
  }
}

async function getAdjustedStaticPaths(paths: StaticExportPath[]) {
  // - Filter 404 and 500 pages from the map service,
  //    they will be handled by 404.tsx and 500.tsx using NextJS custom error pages
  // - Filter out ISR paths

  // WORKAROUND to save cache in file and have it in ISR function
  // where we miss environment variables and permissions to write files (only in .tmp folder)
  // This will change in Next14 and new runtime.
  await getProductISRPaths()

  const newPaths = paths.filter(({ params }) => {
    const p = String(params.slug?.[0] || '')
    const asPath = `/${params.slug?.join('/') || ''}`
    const isISRPath = isAnISRPath(asPath)
    return !['404', '500'].includes(p) && !isISRPath
  })

  return newPaths
}

function isAnISRPath(asPath: string) {
  return ISRPathHelper().isISRPath(getSiteName(), asPath)
}

export const getStaticProps: GetStaticProps<SlugStaticProps, StaticPropsContext['params']> = async ({
  params,
}): Promise<GetStaticPropsResult<SlugStaticProps>> => {
  const timer = Date.now()
  const siteName = getSiteName()
  const brandName = getBrandName()
  const asPath = `/${params?.slug?.join('/') || ''}`
  const config = await configRepository.get({ siteName, url: asPath })
  const tokens = await tokensRepository.getByBrandName(brandName)
  const translations = await translationsRepository.getFor(config.locale)

  logGetStaticPropsStart(asPath)
  const routeParamsForPath = routeRepository.getRouteParamsFor(siteName, asPath)
  if (routeParamsForPath) {
    return buildStaticProps({
      ...routeParamsForPath,
      asPath: routeParamsForPath?.asPath ?? asPath,
      config,
      translations,
      tokens,
    })
  }

  // TODO replace this exception with a custom route not using Uniform data, this is not needed to be here
  const isGroupsResponsiblePage
    = ['sunwebnl', 'sunwebfr', 'sunwebdk'].includes(siteName) && asPath.startsWith('/groups/responsiblepage')

  if (isGroupsResponsiblePage) {
    return createPropsFullReactNoSitecore('7f45b2c3-1234-56d7-89ab-cdef01234567', config, translations, tokens)
  }

  const ENABLE_PROFILE_PAGE = featureFlagClient.isActive('ENABLE_PROFILE_PAGE')
  if (ENABLE_PROFILE_PAGE) {
    const isProfilePage = ['sunwebnl'].includes(siteName) && asPath.startsWith('/mijn-sunweb/profielpagina')

    if (isProfilePage) {
      return createPropsFullReactNoSitecore('7f45b2c3-1234-56d7-89ab-cdef01234568', config, translations, tokens)
    }
  }
  const ENABLE_MARKETING_PREFERENCES_PAGE = featureFlagClient.isActive('ENABLE_MARKETING_PREFERENCES_PAGE')
  if (ENABLE_MARKETING_PREFERENCES_PAGE) {
    const isMarketingPreferencesPage = ['sunwebnl'].includes(siteName) && asPath.startsWith('/vakantie/marketing-voorkeuren')

    if (isMarketingPreferencesPage) {
      return createPropsFullReactNoSitecore('7f45b2c3-1234-56d7-89ab-cdef01234569', config, translations, tokens, 'Jouw marketingvoorkeuren beheren | Sunweb')
    }
  }

  let props
  try {
    props = (await uniformPageClient.getProps(asPath)).props
  }
  catch (error) {
    if (error instanceof CMSNotFoundError) {
      const currentPathIsAProductPage = await isAProductPage(asPath)

      if (currentPathIsAProductPage) {
        console.warn(error)
        return redirectToParentPage(params!)
      }

      return handleNotFoundPagesWithIsr(asPath)
    }

    console.error(error)
    throw error
  }

  if (isAnISRPath(asPath)) {
    const isrProps = buildStaticPropsForISRPages(props, config, translations, tokens)

    logGetStaticPropsFinished(asPath, timer)

    return isrProps
  }

  // Return for SSG pages no full react
  return {
    props: {
      ...props,
      config,
      translations,
      tokens,
    },
  }
}

function logGetStaticPropsStart(asPath: string) {
  let lambdaInfo = `version: ${process.env.AWS_LAMBDA_FUNCTION_VERSION}, memory: ${process.env.AWS_LAMBDA_FUNCTION_MEMORY_SIZE}`
  lambdaInfo += `, region: ${process.env.AWS_REGION}`
  lambdaInfo += `, initialization: ${process.env.AWS_LAMBDA_INITIALIZATION_TYPE}`
  lambdaInfo += `, environment: ${process.env.AWS_EXECUTION_ENV}`
  // eslint-disable-next-line no-console
  console.log(`-- getStaticProps: ${asPath}. Lambda: ${lambdaInfo}`)
}

function createPropsFullReactNoSitecore(
  pageId: string,
  config: Configuration,
  translations: Translations,
  tokens: Tokens,
  pageTitle?: string,
) {
  return {
    props: {
      cmsFields: {},
      fields: {
        ...(pageTitle && { pagetitle: pageTitle }),
      },
      page: {
        id: pageId,
        fields: {},
      },
      config,
      translations,
      tokens,
    },
  } as { props: SlugMinimumStaticProps }
}

function parentPageSlug(params: StaticPropsContext['params']) {
  return params.slug?.slice(0, params.slug.length - 1).join('/')
}

function redirectToParentPage(params: StaticPropsContext['params']) {
  const enableDomainRedirect = process.env.ENABLE_FORCE_HOST_ON_PARENT_REDIRECT === 'true'
  const websiteDomain = getWebsiteDomain()

  if (enableDomainRedirect && websiteDomain) {
    // This is a workaround to avoid redirect issues in Sunweb.be with multilanguage.
    // Sunweb.be has a different set up with a proxy in front of two Netlify apps.
    // Relative urls have problems, redirecting to internal Netlify app, so we need to do absolute redirect.
    return {
      redirect: {
        destination: `https://${websiteDomain}/${parentPageSlug(params)}?PageNotFound=true`,
        permanent: true,
      },
    }
  }
  return {
    redirect: {
      destination: `/${parentPageSlug(params)}?PageNotFound=true`,
      permanent: true,
    },
  }
}

function handleNotFoundPagesWithIsr(asPath: string) {
  // If we already come from /NotFoundInBlobStorage just return a 404 to avoid an infinite loop
  if (asPath?.toLowerCase().startsWith('/notfoundinblobstorage')) {
    return {
      notFound: true as const,
    }
  }

  // When not found a 308 to /NotFoundInBlobStorage it's returned to be handled by the fallback edge function
  return {
    redirect: {
      destination: '/NotFoundInBlobStorage',
      permanent: true,
    },
  }
}

function buildStaticPropsForISRPages(
  props: UniformClientProps,
  config: Configuration,
  translations: Translations,
  tokens: Tokens,
) {
  const pagetitle = (props.fields.pageTitle as string) ?? ''
  const pagemetadescription = (props.fields.pageMetaDescription as string) ?? ''
  const pageId = props.page?.id || ''
  const revalidationTimeOfISRPagesInSeconds = 600

  return {
    props: {
      ...props,
      config,
      translations,
      tokens,
      cmsFields: {
        ...props.cmsFields,
        contextItemId: props.pageId ?? pageId,
      },
      fields: {
        ...props.fields,
        pagetitle,
        pagemetadescription,
      },
      page: {
        ...props.page,
      },
    },
    revalidate: revalidationTimeOfISRPagesInSeconds,
  }
}

// This maybe should only live in the 'full react not ISR' strategy if we want finally to go for this path
async function buildStaticProps({
  asPath,
  sitecorePageId,
  pageTitle,
  config,
  translations,
  tokens,
}: {
  asPath: string
  sitecorePageId?: string
  pageTitle?: string
  config: Configuration
  translations: Translations
  tokens: Tokens
}): Promise<{ props: SlugMinimumStaticProps }> {
  const { props } = await uniformPageClient.getProps(asPath)
  const id = sitecorePageId ?? props.pageId

  return {
    props: {
      cmsFields: {
        ...props.cmsFields,
        contextItemId: props.pageId ?? '',
      },
      fields: extractFields({ props, pageTitle }),
      page: {
        ...props.page,
        id,
      },
      config,
      translations,
      tokens,
      ...(props.enableAbTest && { enableAbTest: props.enableAbTest }),
    },
  }
}

function extractFields({ props, pageTitle }: { props: UniformClientProps, pageTitle?: string }) {
  const pagetitle = pageTitle ?? props.fields.pageTitle
  let fields = props.fields
  if (pagetitle) {
    fields = { ...props.fields, pagetitle }
  }
  return fields
}

function logGetStaticPropsFinished(asPath: string, timer: number) {
  // eslint-disable-next-line no-console
  console.log(`-- getStaticProps: ${asPath} took ${Date.now() - timer}ms`)
}

export default Page
